/**
 * "Promo codes for this client" — a read-only mini-card on Manage Client.
 * Lists the single-client codes made for THIS client and links to the Promo
 * Codes page (pre-set to this client) to create or switch one off.
 *
 * Mounted with key={clientId}, so state never carries over between clients.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Row {
  code: string;
  status: string;
  percentOff: number | null;
  scope: string;
  endsOn: string | null;
  redeemedCount: number;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active', scheduled: 'Scheduled', expired: 'Expired', used_up: 'Used', off: 'Off', broken: 'Broken record',
};
const SCOPE_LABEL: Record<string, string> = { credits: 'credit packs', platform: 'platform fee', both: 'credits + platform fee' };

export function ClientPromoCodesCard({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<{ clientId: string }, { codes: Row[] }>(functions, 'adminListPromoCodes');
        const res = await fn({ clientId });
        if (!cancelled) setRows(res.data.codes || []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load promo codes');
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  return (
    <div style={{ marginTop: '24px' }}>
      <h2 className="section-title">Promo codes for this client</h2>
      <div style={{ padding: '16px', borderRadius: '8px', background: 'var(--dark-2)', border: '1px solid var(--dark-3)' }}>
        {error ? (
          <p style={{ color: 'var(--red)', margin: 0, fontSize: '0.85rem' }}>{error}</p>
        ) : rows === null ? (
          <p style={{ color: 'var(--gray-400)', margin: 0, fontSize: '0.85rem' }}>Loading...</p>
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--gray-400)', margin: 0, fontSize: '0.85rem' }}>
            No one-client codes for this account. Shared codes work here too.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.9rem' }}>
            {rows.map(r => (
              <li key={r.code} style={{ marginBottom: '4px' }}>
                <strong style={{ letterSpacing: '0.04em' }}>{r.code}</strong> &mdash; {r.percentOff ?? '?'}% off {SCOPE_LABEL[r.scope] || r.scope}
                {' '}&middot; {STATUS_LABEL[r.status] || r.status}
                {r.endsOn ? ` · ends ${r.endsOn}` : ''}
              </li>
            ))}
          </ul>
        )}
        <div style={{ marginTop: '12px' }}>
          <Link to={`/admin/promo-codes?client=${encodeURIComponent(clientId)}`} className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>
            Create or manage codes
          </Link>
        </div>
      </div>
    </div>
  );
}
